import { useState } from "react";
import { Play, X } from "lucide-react";
import { THUMBS } from "../data/content";

function thumbUrl(id) {
  // Pre-resolved Vimeo CDN URL if we have one. Otherwise return null and let the
  // designed yellow-play-button fallback render, that's intentional.
  return THUMBS[id] || null;
}

export default function VideoCard({ video, size = "md", testId }) {
  const [open, setOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const src = thumbUrl(video.id);
  const [errored, setErrored] = useState(!src);

  const aspect = size === "lg" ? "aspect-[16/9]" : size === "tall" ? "aspect-[4/5]" : "aspect-[16/9]";

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={`yv-card ${aspect} group w-full`}
        data-testid={testId || `video-card-${video.id}`}
        aria-label={`Play ${video.title}`}
      >
        {!errored && src ? (
          <img
            src={src}
            alt={video.title}
            loading="lazy"
            onLoad={(e) => {
              if (e.target.naturalWidth < 200) setErrored(true);
              else setLoaded(true);
            }}
            onError={() => setErrored(true)}
            className={`absolute inset-0 w-full h-full object-cover transition duration-700 ${loaded ? "opacity-100" : "opacity-0"} group-hover:scale-105`}
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-[#181818] via-[#242012] to-[#0a0a0a]">
            <div className="text-center px-4">
              <div className="w-16 h-16 rounded-full bg-[var(--yellow)] grid place-items-center mx-auto mb-4 shadow-[0_8px_30px_rgba(207,186,45,0.35)]">
                <Play className="text-black" size={28} fill="currentColor" />
              </div>
              <p className="text-white font-bold text-base leading-tight px-2">{video.title}</p>
              {video.client && <p className="text-[var(--yellow)] text-[10px] uppercase tracking-[0.18em] mt-2 font-semibold">{video.client}</p>}
            </div>
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
        <div className={`yv-play-pill${size === "lg" ? " yv-play-pill-static" : ""}`}>
          <Play size={26} fill="#0a0a0a" />
        </div>
        <div className="yv-card-overlay">
          <p className="yv-eyebrow text-[10px] mb-1">{video.client || "Featured"}</p>
          <p className="text-white font-bold text-base leading-tight">{video.title}</p>
          {video.summary && (
            <p className="text-white/70 text-xs mt-2 line-clamp-2">{video.summary}</p>
          )}
        </div>
        {/* Static caption strip visible without hover (hidden when fallback covers it) */}
        {!errored && (
          <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/95 to-transparent pointer-events-none">
            <p className="text-white font-semibold text-sm truncate" data-testid={`video-card-title-${video.id}`}>
              {video.title}
            </p>
          </div>
        )}
      </button>

      {open && (
        <div className="yv-modal-backdrop" onClick={() => setOpen(false)} data-testid={`video-modal-${video.id}`}>
          <div className="yv-modal" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between p-5 border-b border-white/5">
              <div>
                <p className="yv-eyebrow text-[10px] mb-1">{video.client}</p>
                <h3 className="yv-h3 text-white">{video.title}</h3>
              </div>
              <button
                onClick={() => setOpen(false)}
                className="p-2 text-white/70 hover:text-white rounded-full hover:bg-white/10 transition"
                aria-label="Close"
                data-testid={`video-modal-close-${video.id}`}
              >
                <X size={22} />
              </button>
            </div>
            <div className="relative aspect-video bg-black">
              <iframe
                src={
                  video.youtube
                    ? `https://www.youtube.com/embed/${video.youtube}?autoplay=1&rel=0&modestbranding=1`
                    : `https://player.vimeo.com/video/${video.id}?${video.h ? `h=${video.h}&` : ""}autoplay=1&title=0&byline=0&portrait=0`
                }
                className="absolute inset-0 w-full h-full"
                frameBorder="0"
                allow="autoplay; fullscreen; picture-in-picture; encrypted-media"
                allowFullScreen
                title={video.title}
              />
            </div>
            {video.summary && (
              <div className="p-6 text-white/75 text-sm leading-relaxed max-h-44 overflow-auto">
                {video.summary}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
